import React, { useState, useEffect, useRef, useCallback } from 'react';
import styled, { keyframes, css } from 'styled-components';
import TagSelector, { Tag } from './TagSelector';

// ─── Types ────────────────────────────────────────────────────────────────────

interface CityData {
  id: number;
  nome: string;
  microrregiao: { mesorregiao: { UF: { sigla: string } } };
}

export interface NewContactFormData {
  nome: string;
  telefone: string;
  email: string;
  cidade_id: string;
  data_nascimento: string;
  profissao: string;
  time_coracao: string;
  rede_social: string;
  observacoes: string;
  multiplicador: boolean;
}

interface Props {
  isOpen: boolean;
  onClose: () => void;
  onSubmit: (e: React.FormEvent) => void;
  formData: NewContactFormData;
  onChange: (field: string, value: string | boolean) => void;
  submitting: boolean;
  sortedCities: CityData[];
  selectedTags: Tag[];
  setSelectedTags: (tags: Tag[]) => void;
  allGroupTags: Tag[];
  setAllGroupTags: (tags: Tag[]) => void;
}

// ─── Optional field definitions ───────────────────────────────────────────────

type OptKey = 'email' | 'birthday' | 'profession' | 'team' | 'tags' | 'handle' | 'notes';

const OPTIONAL_FIELDS: { key: OptKey; label: string; icon: React.ReactNode }[] = [
  { key: 'email',      label: 'Email',           icon: <IconMail /> },
  { key: 'birthday',   label: 'Nascimento',      icon: <IconCake /> },
  { key: 'profession', label: 'Profissão',       icon: <IconBriefcase /> },
  { key: 'team',       label: 'Time do coração', icon: <IconFlag /> },
  { key: 'tags',       label: 'Tags',            icon: <IconTag /> },
  { key: 'handle',     label: 'Rede social',     icon: <IconGlobe /> },
  { key: 'notes',      label: 'Notas internas',  icon: <IconNote /> },
];

const TIMES_RS = [
  'Grêmio', 'Internacional', 'Juventude', 'Caxias',
  'Brasil de Pelotas', 'Ypiranga', 'São José (POA)',
  'Esportivo', 'Aimoré', 'São Luiz', 'Guarany de Bagé', 'Outros',
];

// ─── Phone mask ───────────────────────────────────────────────────────────────

function maskPhone(raw: string): string {
  const d = raw.replace(/\D/g, '').slice(0, 11);
  if (!d) return '';
  if (d.length <= 2) return `(${d}`;
  if (d.length <= 6) return `(${d.slice(0, 2)}) ${d.slice(2)}`;
  if (d.length <= 10) return `(${d.slice(0, 2)}) ${d.slice(2, 6)}-${d.slice(6)}`;
  return `(${d.slice(0, 2)}) ${d.slice(2, 7)}-${d.slice(7)}`;
}

function phoneDigits(s: string) { return s.replace(/\D/g, ''); }

// ─── Animations ───────────────────────────────────────────────────────────────

const popIn = keyframes`
  from { transform: translateY(10px) scale(.985); opacity: 0; }
  to   { transform: none; opacity: 1; }
`;

const fieldSlideIn = keyframes`
  from { opacity: 0; transform: translateY(-6px); }
  to   { opacity: 1; transform: translateY(0); }
`;

const tickPop = keyframes`
  from { transform: scale(.4); opacity: 0; }
  to   { transform: scale(1);  opacity: 1; }
`;

const starPop = keyframes`
  0%   { transform: scale(.7) rotate(-25deg); }
  55%  { transform: scale(1.18) rotate(8deg); }
  100% { transform: scale(1) rotate(0); }
`;

// ─── Styled components ────────────────────────────────────────────────────────

const Overlay = styled.div<{ $open: boolean }>`
  position: fixed; inset: 0; z-index: 3000;
  background: radial-gradient(120% 80% at 50% 20%, rgba(11,18,32,.18), rgba(11,18,32,.44));
  display: flex; align-items: center; justify-content: center;
  padding: 24px;
  opacity: ${p => p.$open ? 1 : 0};
  pointer-events: ${p => p.$open ? 'all' : 'none'};
  transition: opacity .2s;
  @media (max-width: 560px) { padding: 0; align-items: flex-end; }
`;

const ModalShell = styled.div`
  width: 520px; max-width: 100%;
  max-height: min(88vh, 780px);
  background: #fff;
  border-radius: 16px;
  box-shadow: 0 1px 1px rgba(16,24,40,.04), 0 12px 32px -8px rgba(16,24,40,.18), 0 24px 60px -12px rgba(16,24,40,.22);
  display: grid;
  grid-template-rows: auto 1fr auto;
  overflow: hidden;
  border: 1px solid #ECEDF0;
  animation: ${popIn} .26s cubic-bezier(.2,.7,.2,1);

  @media (max-width: 560px) {
    border-radius: 16px 16px 0 0;
    width: 100%;
    max-height: 92vh;
    align-self: flex-end;
  }
`;

const MHeader = styled.div`
  display: grid;
  grid-template-columns: 1fr auto;
  align-items: start;
  gap: 14px;
  padding: 18px 22px 16px;
  border-bottom: 1px solid #ECEDF0;
`;

const MTitle = styled.div`
  font-size: 19px; font-weight: 700; letter-spacing: -0.015em;
  color: #0B1220; line-height: 1.2;
`;

const MSub = styled.div`
  color: #8B93A6; font-size: 12.5px; margin-top: 3px;
`;

const CloseBtn = styled.button`
  width: 32px; height: 32px; border-radius: 8px;
  border: 1px solid transparent;
  background: transparent; cursor: pointer; color: #5C6577;
  display: inline-flex; align-items: center; justify-content: center;
  transition: background .15s, color .15s; flex-shrink: 0;
  &:hover { background: #F2F3F6; color: #0B1220; }
`;

const MBody = styled.div`
  padding: 18px 22px 4px;
  overflow-y: auto; min-height: 0;
  &::-webkit-scrollbar { width: 10px; }
  &::-webkit-scrollbar-thumb { background: #DFE1E6; border: 3px solid #fff; border-radius: 999px; }

  @media (max-width: 560px) {
    padding: 14px 16px 4px;
  }
`;

const Grp = styled.div`
  margin-bottom: 14px;
`;

const GrpLabel = styled.label`
  display: flex; align-items: center; gap: 6px;
  font-size: 12.5px; font-weight: 600; color: #2A3142;
  margin-bottom: 6px;
`;

const Req = styled.span<{ $ok?: boolean }>`
  display: inline-flex; align-items: center; gap: 4px;
  font-size: 10.5px; font-weight: 700; letter-spacing: .04em;
  margin-left: auto;
  color: ${p => p.$ok ? '#168A50' : '#8B93A6'};
`;

const Asterisk = styled.span`
  color: #1E5BD8; margin-left: 1px;
`;

const IField = styled.div<{ $valid?: boolean }>`
  position: relative; display: flex; align-items: center;
  background: #fff;
  border: 1px solid ${p => p.$valid ? '#B4E2C5' : '#DFE1E6'};
  border-radius: 10px;
  transition: border-color .15s, box-shadow .15s, background .15s;
  background: ${p => p.$valid ? 'linear-gradient(180deg,#FBFEFC,#fff)' : '#fff'};

  &:hover { border-color: ${p => p.$valid ? '#B4E2C5' : '#C8CDD6'}; }
  &:focus-within {
    border-color: ${p => p.$valid ? '#1FA060' : '#1E5BD8'};
    box-shadow: 0 0 0 3px ${p => p.$valid ? '#DCEFE3' : '#DDE7FC'};
  }
`;

const iInputBase = css`
  flex: 1; min-width: 0;
  border: 0; outline: none; background: transparent;
  font: 400 14px 'Manrope', system-ui, sans-serif;
  color: #0B1220;
  padding: 11px 12px 11px 14px;
  &::placeholder { color: #8B93A6; }
`;

const IInput = styled.input`${iInputBase}`;
const ISelect = styled.select`
  ${iInputBase}
  appearance: none; cursor: pointer; padding-right: 36px;
`;
const ITextArea = styled.textarea`
  ${iInputBase}
  resize: vertical; min-height: 72px; max-height: 200px;
  line-height: 1.5; padding: 11px 14px;
`;

const PhonePrefix = styled.span`
  padding: 0 0 0 14px;
  color: #8B93A6;
  font: 500 13.5px 'JetBrains Mono', ui-monospace, monospace;
  white-space: nowrap;
`;

const PhoneInput = styled(IInput)`
  font: 500 14px 'JetBrains Mono', ui-monospace, monospace;
  padding-left: 8px;
`;

const ISuffix = styled.span`
  position: absolute; right: 10px; top: 50%; transform: translateY(-50%);
  display: inline-flex; align-items: center; gap: 6px;
  pointer-events: none;
`;

const ValidCheck = styled.span`
  width: 20px; height: 20px; border-radius: 50%;
  background: #1FA060; color: #fff;
  display: inline-flex; align-items: center; justify-content: center;
  animation: ${tickPop} .22s cubic-bezier(.2,.8,.2,1);
`;

const ErrHint = styled.div`
  color: #B0271F; font-size: 11.5px; margin-top: 5px; padding-left: 2px;
`;

const Hint = styled.div`
  color: #8B93A6; font-size: 11.5px; margin-top: 5px; padding-left: 2px;
`;

// ─── Searchable combo ─────────────────────────────────────────────────────────

const ComboWrapper = styled.div`position: relative;`;
const ComboTrigger = styled.button<{ $valid?: boolean }>`
  width: 100%; display: flex; align-items: center;
  background: ${p => p.$valid ? 'linear-gradient(180deg,#FBFEFC,#fff)' : '#fff'};
  border: 1px solid ${p => p.$valid ? '#B4E2C5' : '#DFE1E6'};
  border-radius: 10px; padding: 11px 36px 11px 14px;
  font: 400 14px 'Manrope', system-ui, sans-serif;
  color: ${p => p.$valid ? '#0B1220' : '#8B93A6'};
  text-align: left; cursor: pointer;
  transition: border-color .15s, box-shadow .15s; position: relative;
  &:hover { border-color: ${p => p.$valid ? '#B4E2C5' : '#C8CDD6'}; }
  &:focus {
    outline: none;
    border-color: ${p => p.$valid ? '#1FA060' : '#1E5BD8'};
    box-shadow: 0 0 0 3px ${p => p.$valid ? '#DCEFE3' : '#DDE7FC'};
  }
`;
const ComboTriggerSuffix = styled.span`
  position: absolute; right: 10px; top: 50%; transform: translateY(-50%);
  display: inline-flex; align-items: center; gap: 6px; pointer-events: none;
`;
const ComboDropdown = styled.div`
  position: absolute; top: calc(100% + 4px); left: 0; right: 0;
  background: #fff; border: 1px solid #DFE1E6; border-radius: 10px;
  box-shadow: 0 8px 24px rgba(0,0,0,.12); z-index: 10;
  overflow: hidden; display: flex; flex-direction: column; max-height: 240px;
`;
const ComboSearch = styled.input`
  width: 100%; padding: 9px 12px; border: 0; border-bottom: 1px solid #ECEDF0;
  font: 400 13px 'Manrope', system-ui, sans-serif; color: #0B1220; outline: none; box-sizing: border-box;
  &::placeholder { color: #8B93A6; }
  &:focus { border-bottom-color: #1E5BD8; }
`;
const ComboList = styled.div`
  overflow-y: auto; flex: 1;
  &::-webkit-scrollbar { width: 8px; }
  &::-webkit-scrollbar-thumb { background: #DFE1E6; border: 2px solid #fff; border-radius: 999px; }
`;
const ComboOption = styled.div<{ $selected?: boolean }>`
  padding: 9px 14px;
  font: ${p => p.$selected ? '600' : '400'} 13px 'Manrope', system-ui, sans-serif;
  color: ${p => p.$selected ? '#1E5BD8' : '#0B1220'};
  background: ${p => p.$selected ? '#EEF3FE' : 'transparent'};
  cursor: pointer; transition: background .1s;
  &:hover { background: ${p => p.$selected ? '#EEF3FE' : '#F2F3F6'}; }
`;

interface SearchableComboProps {
  value: string;
  placeholder: string;
  options: { value: string; label: string }[];
  onChange: (value: string) => void;
  valid?: boolean;
}
function SearchableCombo({ value, placeholder, options, onChange, valid }: SearchableComboProps) {
  const [open, setOpen] = useState(false);
  const [search, setSearch] = useState('');
  const wrapperRef = useRef<HTMLDivElement>(null);
  const searchRef = useRef<HTMLInputElement>(null);
  const selectedLabel = options.find(o => o.value === value)?.label ?? '';
  const filtered = options.filter(o => o.label.toLowerCase().includes(search.toLowerCase()));
  useEffect(() => {
    if (!open) { setSearch(''); return; }
    setTimeout(() => searchRef.current?.focus(), 30);
  }, [open]);
  useEffect(() => {
    if (!open) return;
    const handler = (e: MouseEvent) => {
      if (!wrapperRef.current?.contains(e.target as Node)) setOpen(false);
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, [open]);
  return (
    <ComboWrapper ref={wrapperRef}>
      <ComboTrigger type="button" $valid={valid} onClick={() => setOpen(o => !o)}
        aria-haspopup="listbox" aria-expanded={open}>
        {selectedLabel || placeholder}
        <ComboTriggerSuffix>
          {valid
            ? <ValidCheck><IconCheck size={12} sw={3.4} /></ValidCheck>
            : <span style={{ color: '#8B93A6' }}><IconChevronDown /></span>}
        </ComboTriggerSuffix>
      </ComboTrigger>
      {open && (
        <ComboDropdown role="listbox">
          <ComboSearch ref={searchRef} placeholder="Buscar…" value={search}
            onChange={e => setSearch(e.target.value)}
            onKeyDown={e => {
              if (e.key === 'Escape') setOpen(false);
              if (e.key === 'Enter' && filtered.length > 0) { onChange(filtered[0].value); setOpen(false); }
            }} />
          <ComboList>
            {value && (
              <ComboOption onClick={() => { onChange(''); setOpen(false); }}>
                <span style={{ color: '#8B93A6' }}>— Limpar seleção</span>
              </ComboOption>
            )}
            {filtered.length === 0 && (
              <ComboOption style={{ color: '#8B93A6', pointerEvents: 'none' }}>Nenhum resultado</ComboOption>
            )}
            {filtered.map(o => (
              <ComboOption key={o.value} $selected={o.value === value}
                onClick={() => { onChange(o.value); setOpen(false); }}
                role="option" aria-selected={o.value === value}>
                {o.label}
              </ComboOption>
            ))}
          </ComboList>
        </ComboDropdown>
      )}
    </ComboWrapper>
  );
}

// ─── Optional field tray ──────────────────────────────────────────────────────

const AddTray = styled.div<{ $done?: boolean }>`
  margin-top: 18px;
  padding: ${p => p.$done ? '11px 14px' : '13px 14px 12px'};
  background: ${p => p.$done ? '#E8F6EE' : '#FAFAFB'};
  border: 1px solid ${p => p.$done ? '#B4E2C5' : '#ECEDF0'};
  border-radius: 12px;
  transition: padding .2s, background .2s;
`;

const TrayHeader = styled.div`
  display: flex; align-items: center; gap: 8px;
  margin-bottom: 10px;
`;

const TrayTitle = styled.span`
  font-size: 11px; font-weight: 700; letter-spacing: .08em; text-transform: uppercase;
  color: #5C6577;
  display: inline-flex; align-items: center; gap: 6px;
`;

const TrayHint = styled.span`
  margin-left: auto;
  font-size: 11px; font-weight: 600; color: #8B93A6;
`;

const TrayChips = styled.div`
  display: flex; flex-wrap: wrap; gap: 6px;
`;

const AddChip = styled.button`
  display: inline-flex; align-items: center; gap: 7px;
  padding: 6px 12px 6px 6px;
  border: 1px dashed #DFE1E6;
  background: #fff; border-radius: 999px;
  color: #2A3142;
  font: 600 12.5px 'Manrope', system-ui, sans-serif;
  cursor: pointer;
  transition: background .15s, border-color .15s, color .15s, box-shadow .15s;
  &:hover {
    background: #EEF3FE; border-style: solid; border-color: #BCD0FA;
    color: #1B50C2;
    box-shadow: 0 4px 10px -6px rgba(30,91,216,.25);
  }
`;

const ChipPlus = styled.span`
  width: 18px; height: 18px; border-radius: 50%;
  background: #F2F3F6; color: #5C6577;
  display: inline-flex; align-items: center; justify-content: center;
  transition: background .15s, color .15s;
  ${AddChip}:hover & {
    background: #DDE7FC; color: #1E5BD8;
  }
`;

const ChipIcon = styled.span`
  color: #8B93A6; display: inline-flex;
  ${AddChip}:hover & { color: #1B50C2; }
`;

const TrayDone = styled.div`
  display: inline-flex; align-items: center; gap: 8px;
  font-size: 12px; font-weight: 600; color: #1B6B3A;
`;

// ─── Active optional field ─────────────────────────────────────────────────────

const ActField = styled.div`
  margin-bottom: 14px;
  animation: ${fieldSlideIn} .28s cubic-bezier(.2,.7,.2,1);
`;

const ActHeader = styled.div`
  display: flex; align-items: center; gap: 6px;
  margin-bottom: 6px;
`;

const ActLabel = styled.label`
  display: inline-flex; align-items: center; gap: 7px;
  font-size: 12.5px; font-weight: 600; color: #2A3142;
`;

const ActRemove = styled.button`
  margin-left: auto;
  height: 22px; padding: 0 8px 0 6px; border-radius: 6px;
  border: 0; background: transparent;
  color: #8B93A6; cursor: pointer;
  display: inline-flex; align-items: center; gap: 3px;
  font: 600 11px 'Manrope', system-ui, sans-serif;
  transition: background .15s, color .15s;
  &:hover { background: #FDECEC; color: #B0271F; }
`;

// ─── Multiplier card ──────────────────────────────────────────────────────────

const MpCard = styled.div<{ $on: boolean }>`
  display: grid; grid-template-columns: 40px 1fr auto;
  align-items: center; gap: 14px;
  padding: 14px 16px;
  border: 1px solid ${p => p.$on ? '#F2C75D' : '#DFE1E6'};
  border-radius: 12px;
  background: ${p => p.$on
    ? 'radial-gradient(120% 100% at 100% 0%,rgba(252,207,93,.35),transparent 55%),linear-gradient(180deg,#FFF8E5,#fff)'
    : '#fff'};
  box-shadow: ${p => p.$on ? '0 1px 0 rgba(255,255,255,.7) inset, 0 6px 18px -8px rgba(220,153,32,.45)' : 'none'};
  cursor: pointer; user-select: none;
  transition: border-color .2s, background .2s, box-shadow .2s;
  &:active { transform: translateY(1px); }
`;

const MpIco = styled.span<{ $on: boolean }>`
  width: 40px; height: 40px; border-radius: 12px;
  background: ${p => p.$on
    ? 'linear-gradient(180deg,#F8C950,#DB9A1F)'
    : '#F2F3F6'};
  color: ${p => p.$on ? '#fff' : '#8B93A6'};
  display: inline-flex; align-items: center; justify-content: center;
  transition: background .25s, color .25s;
  ${p => p.$on && css`animation: ${starPop} .5s cubic-bezier(.2,.8,.2,1);`}
  box-shadow: ${p => p.$on ? '0 1px 0 rgba(255,255,255,.5) inset, 0 4px 12px -2px rgba(220,153,32,.55)' : 'none'};
`;

const MpText = styled.div``;
const MpTitle = styled.span<{ $on: boolean }>`
  display: block; font-size: 13.5px; font-weight: 700;
  color: ${p => p.$on ? '#7A4F00' : '#0B1220'}; letter-spacing: -.005em;
`;
const MpHint = styled.span<{ $on: boolean }>`
  display: block; font-size: 11.5px;
  color: ${p => p.$on ? '#9F6F1B' : '#8B93A6'}; margin-top: 1px;
`;

const MpSwitch = styled.span<{ $on: boolean }>`
  position: relative; width: 38px; height: 22px; border-radius: 99px;
  background: ${p => p.$on ? 'linear-gradient(135deg,#F4B73F,#C8901F)' : '#DADDE3'};
  flex-shrink: 0;
  transition: background .2s;
  &::after {
    content: ''; position: absolute; top: 2px;
    left: ${p => p.$on ? '18px' : '2px'};
    width: 18px; height: 18px; border-radius: 50%; background: #fff;
    box-shadow: 0 1px 2px rgba(0,0,0,.18);
    transition: left .22s cubic-bezier(.2,.8,.2,1);
  }
`;

// ─── Social handle field ──────────────────────────────────────────────────────

const HandleAt = styled.span`
  width: 38px; height: 100%;
  display: inline-flex; align-items: center; justify-content: center;
  color: #5C6577; font: 700 15px 'Manrope', system-ui, sans-serif;
  border-right: 1px solid #DFE1E6;
  background: #FAFAFB;
  border-radius: 10px 0 0 10px; align-self: stretch;
  padding: 0;
`;

const HandleNets = styled.span`
  display: inline-flex; align-items: center; gap: 4px;
  padding: 4px 6px 4px 4px;
`;

const NetBtn = styled.button<{ $color?: string }>`
  width: 26px; height: 26px; border-radius: 7px;
  display: inline-flex; align-items: center; justify-content: center;
  color: #fff;
  background: ${p => p.$color || '#DADDE3'};
  border: 0; cursor: pointer;
  transition: transform .12s, filter .15s;
  &:hover:not(:disabled) { transform: translateY(-1px); }
  &:disabled { filter: grayscale(.85) opacity(.5); cursor: not-allowed; }
`;

// ─── Footer ───────────────────────────────────────────────────────────────────

const MFooter = styled.div`
  display: flex; align-items: center; justify-content: flex-end;
  gap: 12px; padding: 14px 22px;
  border-top: 1px solid #ECEDF0;
  background: linear-gradient(180deg, #fff, #FAFAFB);
`;

const FooterHint = styled.span`
  font-size: 11.5px; color: #8B93A6;
  display: inline-flex; align-items: center; gap: 6px;
  @media (max-width: 560px) { display: none; }
`;

const Kbd = styled.span`
  font: 600 10px 'JetBrains Mono', monospace; color: #5C6577;
  background: #F2F3F6; border: 1px solid #DFE1E6;
  border-radius: 4px; padding: 1px 5px;
`;

const FooterBtns = styled.div`
  display: flex; gap: 8px;
`;

const Btn = styled.button`
  display: inline-flex; align-items: center; gap: 8px;
  height: 36px; padding: 0 16px; border-radius: 9px;
  border: 1px solid #DFE1E6;
  background: #fff; color: #0B1220;
  font: 600 13.5px 'Manrope', system-ui, sans-serif;
  cursor: pointer; transition: background .15s;
  &:hover { background: #F2F3F6; }
`;

const BtnPrimary = styled(Btn)`
  background: #1E5BD8; border-color: #1E5BD8; color: #fff;
  box-shadow: 0 1px 2px rgba(30,91,216,.35), 0 1px 0 rgba(255,255,255,.18) inset;
  &:hover:not(:disabled) { background: #1B50C2; }
  &:disabled {
    background: #DDE7FC; border-color: #DDE7FC; color: #fff;
    cursor: not-allowed; box-shadow: none;
  }
`;

// ─── Row 2 cols ───────────────────────────────────────────────────────────────

const Row2 = styled.div`
  display: grid; grid-template-columns: 1fr 1fr; gap: 10px;
  @media (max-width: 560px) { grid-template-columns: 1fr; }
`;

// ─── Icon components ──────────────────────────────────────────────────────────

function IconX() {
  return <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M6 6l12 12M18 6L6 18"/></svg>;
}
function IconCheck({ size = 12, sw = 2.8 }: { size?: number; sw?: number }) {
  return <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={sw} strokeLinecap="round" strokeLinejoin="round"><path d="M4 12l5 5L20 6"/></svg>;
}
function IconPlus({ size = 13 }: { size?: number }) {
  return <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.4" strokeLinecap="round" strokeLinejoin="round"><path d="M12 5v14M5 12h14"/></svg>;
}
function IconChevronDown() {
  return <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M6 9l6 6 6-6"/></svg>;
}
function IconMail() {
  return <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"><rect x="3" y="5" width="18" height="14" rx="2"/><path d="M3 7l9 6 9-6"/></svg>;
}
function IconCake() {
  return <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"><path d="M3 21h18v-2H3v2zM5 17h14v-6c0-1-1-2-2-2H7c-1 0-2 1-2 2v6z"/><path d="M9 9V6M12 9V6M15 9V6"/></svg>;
}
function IconBriefcase() {
  return <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"><rect x="3" y="7" width="18" height="13" rx="2"/><path d="M9 7V5a2 2 0 012-2h2a2 2 0 012 2v2"/></svg>;
}
function IconFlag() {
  return <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"><path d="M5 21V4h11l-2 4 2 4H5"/></svg>;
}
function IconTag() {
  return <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"><path d="M3 12V4a1 1 0 011-1h8l9 9-9 9z"/><circle cx="7.5" cy="7.5" r="1.2"/></svg>;
}
function IconStar() {
  return <svg width="18" height="18" viewBox="0 0 20 20" fill="currentColor"><path d="M9.049 2.927c.3-.921 1.603-.921 1.902 0l1.07 3.292a1 1 0 00.95.69h3.462c.969 0 1.371 1.24.588 1.81l-2.8 2.034a1 1 0 00-.364 1.118l1.07 3.292c.3.921-.755 1.688-1.54 1.118l-2.8-2.034a1 1 0 00-1.175 0l-2.8 2.034c-.784.57-1.838-.197-1.539-1.118l1.07-3.292a1 1 0 00-.364-1.118L2.98 8.72c-.783-.57-.38-1.81.588-1.81h3.461a1 1 0 00.951-.69l1.07-3.292z"/></svg>;
}
function IconGlobe() {
  return <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"><circle cx="12" cy="12" r="9"/><path d="M3 12h18M12 3a14 14 0 010 18M12 3a14 14 0 000 18"/></svg>;
}
function IconNote() {
  return <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"><path d="M5 4h11l3 3v13a1 1 0 01-1 1H5a1 1 0 01-1-1V5a1 1 0 011-1z"/><path d="M8 11h8M8 15h5"/></svg>;
}
function IconSparkles() {
  return <svg width="11" height="11" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"><path d="M12 3l1.5 4.5L18 9l-4.5 1.5L12 15l-1.5-4.5L6 9l4.5-1.5z"/><path d="M19 16l.7 2L22 19l-2.3 1L19 22l-.7-2L16 19l2.3-1z"/></svg>;
}
function IconInstagram() {
  return <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"><rect x="3" y="3" width="18" height="18" rx="5"/><circle cx="12" cy="12" r="4"/><circle cx="17.5" cy="6.5" r="1" fill="currentColor"/></svg>;
}
function IconFacebook() {
  return <svg width="13" height="13" viewBox="0 0 24 24" fill="currentColor"><path d="M22 12.07C22 6.51 17.52 2 12 2S2 6.51 2 12.07c0 5 3.66 9.14 8.44 9.93v-7.03H7.9v-2.9h2.54V9.85c0-2.51 1.49-3.9 3.77-3.9 1.1 0 2.24.2 2.24.2v2.46h-1.26c-1.24 0-1.63.77-1.63 1.56v1.87h2.78l-.44 2.9h-2.34V22c4.78-.79 8.44-4.93 8.44-9.93z"/></svg>;
}

// ─── Main component ───────────────────────────────────────────────────────────

const NewContactModal: React.FC<Props> = ({
  isOpen, onClose, onSubmit,
  formData, onChange,
  submitting, sortedCities,
  selectedTags, setSelectedTags,
  allGroupTags, setAllGroupTags,
}) => {
  const [submitted, setSubmitted] = useState(false);
  const [activeOpt, setActiveOpt] = useState<OptKey[]>([]);
  const nameRef = useRef<HTMLInputElement>(null);

  // Validation
  const v = {
    name: formData.nome.trim().length >= 2,
    phone: phoneDigits(formData.telefone).length >= 10,
    city: !!formData.cidade_id,
  };
  const allValid = v.name && v.phone && v.city;

  // Body scroll lock
  useEffect(() => {
    if (isOpen) {
      document.body.style.overflow = 'hidden';
      return () => { document.body.style.overflow = ''; };
    }
  }, [isOpen]);

  // Focus name on open; reset state on close
  useEffect(() => {
    if (isOpen) {
      setTimeout(() => nameRef.current?.focus(), 50);
      setSubmitted(false);
      setActiveOpt([]);
    }
  }, [isOpen]);

  // Keyboard shortcuts
  useEffect(() => {
    if (!isOpen) return;
    const handler = (e: KeyboardEvent) => {
      if (e.key === 'Escape') { onClose(); return; }
      if ((e.metaKey || e.ctrlKey) && e.key === 'Enter') {
        e.preventDefault();
        setSubmitted(true);
        if (allValid) onSubmit(e as any);
      }
    };
    window.addEventListener('keydown', handler);
    return () => window.removeEventListener('keydown', handler);
  }, [isOpen, allValid, onSubmit, onClose]);

  const addOpt = useCallback((key: OptKey) => {
    setActiveOpt(prev => prev.includes(key) ? prev : [...prev, key]);
  }, []);

  const removeOpt = useCallback((key: OptKey) => {
    setActiveOpt(prev => prev.filter(k => k !== key));
    switch (key) {
      case 'email':      onChange('email', ''); break;
      case 'birthday':   onChange('data_nascimento', ''); break;
      case 'profession': onChange('profissao', ''); break;
      case 'team':       onChange('time_coracao', ''); break;
      case 'tags':       setSelectedTags([]); break;
      case 'handle':     onChange('rede_social', ''); break;
      case 'notes':      onChange('observacoes', ''); break;
    }
  }, [onChange, setSelectedTags]);

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    setSubmitted(true);
    if (!allValid) return;
    onSubmit(e);
  };

  const remaining = OPTIONAL_FIELDS.filter(f => !activeOpt.includes(f.key));

  if (!isOpen) return null;

  return (
    <Overlay $open={isOpen} onClick={onClose}>
      <ModalShell onClick={e => e.stopPropagation()}>
        {/* ── Header ── */}
        <MHeader>
          <div>
            <MTitle>Novo contato</MTitle>
            <MSub>Preencha o essencial — você pode completar o resto depois.</MSub>
          </div>
          <CloseBtn type="button" onClick={onClose} aria-label="Fechar">
            <IconX />
          </CloseBtn>
        </MHeader>

        {/* ── Body ── */}
        <MBody>
          <form id="new-contact-form" onSubmit={handleSubmit} noValidate>

            {/* Nome */}
            <Grp>
              <GrpLabel htmlFor="nc-nome">
                Nome<Asterisk>*</Asterisk>
                <Req $ok={v.name}>
                  {v.name ? <><IconCheck size={11} sw={2.8} />OK</> : 'Obrigatório'}
                </Req>
              </GrpLabel>
              <IField $valid={v.name}>
                <IInput
                  ref={nameRef}
                  id="nc-nome"
                  type="text"
                  placeholder="Nome completo"
                  value={formData.nome}
                  onChange={e => onChange('nome', e.target.value)}
                />
                {v.name && (
                  <ISuffix>
                    <ValidCheck><IconCheck size={12} sw={3.4} /></ValidCheck>
                  </ISuffix>
                )}
              </IField>
              {submitted && !v.name && <ErrHint>Informe o nome completo (mínimo 2 letras).</ErrHint>}
            </Grp>

            {/* Telefone */}
            <Grp>
              <GrpLabel htmlFor="nc-telefone">
                Telefone<Asterisk>*</Asterisk>
                <Req $ok={v.phone}>
                  {v.phone ? <><IconCheck size={11} sw={2.8} />OK</> : 'Obrigatório'}
                </Req>
              </GrpLabel>
              <IField $valid={v.phone}>
                <PhonePrefix>+55</PhonePrefix>
                <PhoneInput
                  id="nc-telefone"
                  type="tel"
                  inputMode="tel"
                  placeholder="(00) 00000-0000"
                  value={formData.telefone}
                  onChange={e => onChange('telefone', maskPhone(e.target.value))}
                />
                {v.phone && (
                  <ISuffix>
                    <ValidCheck><IconCheck size={12} sw={3.4} /></ValidCheck>
                  </ISuffix>
                )}
              </IField>
              {submitted && !v.phone
                ? <ErrHint>Telefone incompleto.</ErrHint>
                : !v.phone && <Hint>DDD + número, com pelo menos 10 dígitos.</Hint>}
            </Grp>

            {/* Cidade */}
            <Grp>
              <GrpLabel>
                Cidade<Asterisk>*</Asterisk>
                <Req $ok={v.city}>
                  {v.city ? <><IconCheck size={11} sw={2.8} />OK</> : 'Obrigatório'}
                </Req>
              </GrpLabel>
              <SearchableCombo
                value={formData.cidade_id}
                placeholder="Selecione uma cidade"
                options={sortedCities.map(c => ({ value: String(c.id), label: `${c.nome} - ${c.microrregiao.mesorregiao.UF.sigla}` }))}
                onChange={val => onChange('cidade_id', val)}
                valid={v.city}
              />
              {submitted && !v.city && <ErrHint>Selecione a cidade.</ErrHint>}
            </Grp>

            {/* Multiplicador — sempre visível */}
            <Grp>
              <MpCard
                $on={formData.multiplicador}
                onClick={() => onChange('multiplicador', !formData.multiplicador)}
                role="switch"
                aria-checked={formData.multiplicador}
              >
                <MpIco $on={formData.multiplicador}><IconStar /></MpIco>
                <MpText>
                  <MpTitle $on={formData.multiplicador}>Multiplicador Político</MpTitle>
                  <MpHint $on={formData.multiplicador}>Aparece destacado na lista e nos relatórios.</MpHint>
                </MpText>
                <MpSwitch $on={formData.multiplicador} />
              </MpCard>
            </Grp>

            {/* ── Active optional fields ── */}
            {activeOpt.map(key => (
              <OptionalField
                key={key}
                fieldKey={key}
                formData={formData}
                onChange={onChange}
                selectedTags={selectedTags}
                setSelectedTags={setSelectedTags}
                allGroupTags={allGroupTags}
                setAllGroupTags={setAllGroupTags}
                onRemove={() => removeOpt(key)}
              />
            ))}

            {/* ── Add-more tray ── */}
            {remaining.length === 0 ? (
              <AddTray $done>
                <TrayDone>
                  <IconCheck size={12} sw={2.8} />
                  Pronto — todos os detalhes opcionais foram adicionados.
                </TrayDone>
              </AddTray>
            ) : (
              <AddTray>
                <TrayHeader>
                  <TrayTitle><IconSparkles />Adicionar mais detalhes</TrayTitle>
                  <TrayHint>opcional · escolha o que precisar</TrayHint>
                </TrayHeader>
                <TrayChips>
                  {remaining.map(f => (
                    <AddChip key={f.key} type="button" onClick={() => addOpt(f.key)}>
                      <ChipPlus><IconPlus size={11} /></ChipPlus>
                      <ChipIcon>{f.icon}</ChipIcon>
                      {f.label}
                    </AddChip>
                  ))}
                </TrayChips>
              </AddTray>
            )}
          </form>
        </MBody>

        {/* ── Footer ── */}
        <MFooter>
          <FooterBtns>
            <Btn type="button" onClick={onClose} disabled={submitting}>Cancelar</Btn>
            <BtnPrimary
              type="submit"
              form="new-contact-form"
              disabled={!allValid || submitting}
            >
              <IconPlus size={13} />
              {submitting ? 'Criando…' : 'Criar contato'}
            </BtnPrimary>
          </FooterBtns>
        </MFooter>
      </ModalShell>
    </Overlay>
  );
};

// ─── Individual optional field renderer ───────────────────────────────────────

interface OptFieldProps {
  fieldKey: OptKey;
  formData: NewContactFormData;
  onChange: (field: string, value: string | boolean) => void;
  selectedTags: Tag[];
  setSelectedTags: (tags: Tag[]) => void;
  allGroupTags: Tag[];
  setAllGroupTags: (tags: Tag[]) => void;
  onRemove: () => void;
}

const TIMES_RS_LIST = [
  'Grêmio', 'Internacional', 'Juventude', 'Caxias',
  'Brasil de Pelotas', 'Ypiranga', 'São José (POA)',
  'Esportivo', 'Aimoré', 'São Luiz', 'Guarany de Bagé', 'Outros',
];

function OptionalField({ fieldKey, formData, onChange, selectedTags, setSelectedTags, allGroupTags, setAllGroupTags, onRemove }: OptFieldProps) {
  const def = OPTIONAL_FIELDS.find(d => d.key === fieldKey)!;
  const inputId = `nc-opt-${fieldKey}`;

  let inner: React.ReactNode = null;

  switch (fieldKey) {
    case 'email':
      inner = (
        <IField>
          <IInput id={inputId} type="email" placeholder="email@exemplo.com"
            value={formData.email} onChange={e => onChange('email', e.target.value)} autoFocus />
        </IField>
      );
      break;

    case 'birthday':
      inner = (
        <IField>
          <IInput id={inputId} type="date"
            value={formData.data_nascimento} onChange={e => onChange('data_nascimento', e.target.value)} autoFocus />
        </IField>
      );
      break;

    case 'profession':
      inner = (
        <IField>
          <IInput id={inputId} type="text" placeholder="Vereador, secretário, agricultor…"
            value={formData.profissao} onChange={e => onChange('profissao', e.target.value)} autoFocus />
        </IField>
      );
      break;

    case 'team':
      inner = (
        <SearchableCombo
          value={formData.time_coracao}
          placeholder="Selecione um time"
          options={TIMES_RS_LIST.map(t => ({ value: t, label: t }))}
          onChange={val => onChange('time_coracao', val)}
          valid={!!formData.time_coracao}
        />
      );
      break;

    case 'tags':
      inner = (
        <TagSelector
          selectedTags={selectedTags}
          onChange={(tags) => { setSelectedTags(tags); }}
          onTagCreated={(tag) => setAllGroupTags([...allGroupTags, tag].sort((a, b) => a.nome.localeCompare(b.nome)))}
        />
      );
      break;

    case 'handle':
      inner = (
        <IField>
          <HandleAt>@</HandleAt>
          <IInput
            id={inputId}
            type="text"
            placeholder="usuario"
            value={formData.rede_social.replace(/^@/, '')}
            onChange={e => onChange('rede_social', e.target.value.replace(/^@/, '').replace(/\s+/g, ''))}
            style={{ paddingLeft: 8 }}
            autoFocus
          />
        </IField>
      );
      break;

    case 'notes':
      inner = (
        <IField>
          <ITextArea
            id={inputId}
            placeholder="Indicações, preferências, contexto da campanha…"
            value={formData.observacoes}
            onChange={e => onChange('observacoes', e.target.value)}
            rows={3}
            autoFocus
          />
        </IField>
      );
      break;

    default:
      return null;
  }

  return (
    <ActField>
      <ActHeader>
        <ActLabel htmlFor={fieldKey !== 'tags' ? inputId : undefined}>
          <span style={{ color: '#8B93A6', display: 'inline-flex' }}>{def.icon}</span>
          {def.label}
        </ActLabel>
        <ActRemove type="button" onClick={onRemove} aria-label={`Remover ${def.label}`}>
          <IconX />Remover
        </ActRemove>
      </ActHeader>
      {inner}
    </ActField>
  );
}

export default NewContactModal;
